// FTEDN Creator — Community Post Card (wired with real like)
import { useState } from 'react';
import { formatRelativeTime } from './community.helpers';
import { useToast } from '../../components/ui/Toast';
import './CommunityPostCard.css';

interface Post {
  id: string;
  author: string;
  avatar: string;
  level: number;
  content: string;
  likes: number;
  comments: number;
  time: string;
  type: string;
  xp: number;
  userLiked?: boolean;
}

interface PostCardProps {
  post: Post;
  index?: number;
  onLike?: () => Promise<boolean>;
}

export function CommunityPostCard({ post, index = 0, onLike }: PostCardProps) {
  const [liked, setLiked] = useState(post.userLiked ?? false);
  const [likeCount, setLikeCount] = useState(post.likes);
  const { addToast } = useToast();

  const handleLike = async () => {
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount(prev => wasLiked ? prev - 1 : prev + 1);

    if (onLike) {
      const ok = await onLike();
      if (!ok) {
        setLiked(wasLiked);
        setLikeCount(prev => wasLiked ? prev + 1 : prev - 1);
        addToast({ type: 'error', title: 'Failed', message: 'Could not like post' });
      }
    }
  };

  const typeColors: Record<string, string> = {
    discussion: 'var(--sage-green)',
    question: 'var(--xp-gold)',
    announcement: 'var(--xp-purple)',
  };

  const displayTime = post.time.includes('ago') ? post.time : formatRelativeTime(post.time);

  return (
    <div className="post-card glass-card" style={{ animationDelay: `${index * 0.08}s` }}>
      <div className="post-header">
        <div className="post-author">
          <div className="post-avatar-wrap">
            <span className="post-avatar">{post.avatar}</span>
            <div className="post-avatar-ring" style={{ borderColor: typeColors[post.type] || 'var(--sage-green)' }} />
          </div>
          <div className="post-author-info">
            <span className="post-author-name">{post.author}</span>
            <span className="post-author-level">Lv.{post.level}</span>
          </div>
        </div>
        <div className="post-meta">
          <span className="post-type-badge" style={{ color: typeColors[post.type], borderColor: typeColors[post.type] }}>
            {post.type}
          </span>
          <span className="post-time">{displayTime}</span>
        </div>
      </div>

      <p className="post-content">{post.content}</p>

      <div className="post-actions">
        <button className={`post-action-btn ${liked ? 'liked' : ''}`} onClick={handleLike}>
          <span className="post-action-icon">{liked ? '❤️' : '🤍'}</span>
          <span>{likeCount}</span>
        </button>
        <button className="post-action-btn">
          <span className="post-action-icon">💬</span>
          <span>{post.comments}</span>
        </button>
        <button className="post-action-btn">
          <span className="post-action-icon">↗️</span>
          <span>Share</span>
        </button>
      </div>
    </div>
  );
}
